import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AnyAction as BaseAction } from 'redux';
import { TenantConfigViewData, TenantConfigViewItem, ViewDefnState } from 'src/dao/tenantConfigClient';
import { ViewDataState } from 'src/types/Domain';
import { AppState, AppThunkDispatch } from 'src/store';
import service from 'src/ServiceContainer';
import Axios from 'src/services/axios';
import { concat, defaultTo, findIndex, get, isEmpty, isNil, isString, reject, set } from 'lodash';
import { ClientDataApi, isListDataApi } from 'src/services/configuration/codecs/confdefnView';
import { getLocalConfig } from '../ViewConfiguratorModal/ViewConfiguratorModal.utils';
import { ConfigurableGridOwnProps } from './ConfigurableGrid.types';
import { BasicItem, BasicPivotItem } from 'src/worker/pivotWorker.types';
import { parseFloorsetDropdownData } from './utils/ConfigurableGrid.utils';
import { ComponentErrorType, ErrorBoundaryComponentError } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { ConfDefnComponentType, isWorklistActive } from 'src/services/configuration/codecs/confdefnComponents';
import { ASSORTMENT } from 'src/utils/Domain/Constants';
import { CoarseEditPayload } from 'src/dao/pivotClient';
import { ConfigurableGridViewDefn } from 'src/services/configuration/codecs/viewdefns/viewdefn';
import { formatViewDefnError } from 'src/services/configuration/codecs/viewdefns/general';
import { receiveTopMemberDropdownData, updateGroupBy, updateConfigureSelections } from '../Subheader/Subheader.slice';
import { parseConfigureConfig } from '../Configure/Configure';
import { AppType } from 'src/services/configuration/codecs/bindings.types';
import coalesce from 'src/utils/Functions/Coalesce';
import { getGroupBySelectedOptionProperty } from 'src/utils/Pivot/Sort';
import { getExtraPivotParams } from '../Subheader/Subheader.container';
import type { AssortmentScopeSelectorSlice as ScopeSlice } from 'src/components/AssortmentScopeSelector/AssortmentScopeSelector.slice';
import { topMemInUrl } from 'src/utils/Functions/epicsFunctions';

export interface ConfigurableGridGroupBySelection {
  selectedIndex: number;
  option: TenantConfigViewItem;
}

export interface ConfigurableGridSlice {
  viewDefnState: ViewDefnState;
  floorsetDataState: ViewDataState;
  gridData: BasicPivotItem[];
  gridDataState: ViewDataState;
  unmodifiedViewDefn: any;
  viewDefn: any;
  topAttributesState: ViewDataState;
  topAttributesData: BasicPivotItem | undefined;
}

const initialState: ConfigurableGridSlice = {
  viewDefnState: ViewDefnState.idle,
  floorsetDataState: ViewDataState.idle,
  gridData: [],
  gridDataState: ViewDataState.idle,
  unmodifiedViewDefn: undefined,
  viewDefn: undefined,
  topAttributesState: ViewDataState.idle,
  topAttributesData: undefined,
};

const configurableGridSlice = createSlice({
  name: 'ConfigurableGrid',
  initialState,
  reducers: {
    requestConfigurableGridConfig(state) {
      state.viewDefnState = ViewDefnState.loading;
    },
    receiveConfigurableGridConfig(state, action: PayloadAction<{ viewDefn: any; unmodifiedViewDefn: any }>) {
      state.viewDefnState = ViewDefnState.loaded;
      state.unmodifiedViewDefn = action.payload.unmodifiedViewDefn;
      state.viewDefn = action.payload.viewDefn;
    },
    updateConfigurableGridConfig(state, action: PayloadAction<any>) {
      state.viewDefn = action.payload;
    },
    requestGridData(state) {
      state.gridDataState = ViewDataState.liveDataLoadingNoCache;
    },
    receiveGridData(state, action: PayloadAction<BasicPivotItem[]>) {
      state.gridDataState = ViewDataState.liveDataReady;
      state.gridData = action.payload;
    },
    receiveTopAttributesData(state, action: PayloadAction<BasicPivotItem>) {
      state.topAttributesState = ViewDataState.liveDataReady;
      state.topAttributesData = action.payload;
    },
    syncGridData(state, action: PayloadAction<{ redecoratedData: BasicPivotItem[]; idProp: string }>) {
      const { redecoratedData, idProp } = action.payload;
      const unsyncedData = state.gridData;
      const syncedData = unsyncedData.map((dataItem) => {
        const syncedDataItem = redecoratedData.find((dd) => dd[idProp] === dataItem[idProp]);
        return isNil(syncedDataItem) ? dataItem : syncedDataItem;
      });

      state.gridData = syncedData;
    },
    requestFloorsetData(state) {
      state.floorsetDataState = ViewDataState.liveDataLoadingNoCache;
    },
    receiveFloorsetData(state) {
      state.floorsetDataState = ViewDataState.liveDataReady;
    },
    requestTopAttributesData(state) {
      state.topAttributesData = undefined;
    },
    refreshConfigurableGridData: () => {
      // nothing to do here, this action triggers refetch in epic
    },
    receiveError: (_state, _action: PayloadAction<ErrorBoundaryComponentError>) => {
      return initialState;
    },
    cleanUp: () => {
      return initialState;
    },
  },
});

export const {
  requestConfigurableGridConfig,
  receiveConfigurableGridConfig,
  updateConfigurableGridConfig,
  requestGridData,
  receiveGridData,
  syncGridData,
  requestFloorsetData,
  receiveFloorsetData,
  requestTopAttributesData,
  receiveTopAttributesData,
  refreshConfigurableGridData,
  receiveError,
  cleanUp,
} = configurableGridSlice.actions;

export function fetchConfigurableGridConfigs(configApi: ClientDataApi) {
  return async (dispatch: AppThunkDispatch, getState: () => AppState): Promise<BaseAction | void> => {
    const defnId = get(configApi.params, 'defnId', '_CONFIGGRID_MISSINGDEFNID_');
    const appName = get(configApi.params, 'appName', AppType.Assortment) as AppType;

    try {
      const configResponse = await service.tenantConfigClient.getTenantViewDefnsWithFavorites({
        defnIds: [defnId],
        appName,
        validationSchemas: [ConfigurableGridViewDefn],
      });
      // TODO: type response, then fix slice type as well
      const config: any = configResponse[0];
      const localConfig = getLocalConfig(
        defnId,
        (configResponse as any)[1],
        dispatch,
        configResponse[0] as unknown as TenantConfigViewData,
      );

      if (config.subheaderDropdowns && config.subheaderDropdowns.groupBy) {
        const currentSelection = getState().subheader.groupBy.selection;
        const currentOptions = getState().subheader.groupBy.options;
        const newOptions = config.subheaderDropdowns.groupBy.options as TenantConfigViewItem[];
        const isContainCurrentOption =
          currentSelection &&
          newOptions.findIndex((option) => option.dataIndex === currentOptions[currentSelection].dataIndex);
        if (isContainCurrentOption) {
          dispatch(
            updateGroupBy({
              selection: isContainCurrentOption,
              options: config.subheaderDropdowns.groupBy.options,
            }),
          );
        } else {
          dispatch(
            updateGroupBy({
              selection: 0,
              options: config.subheaderDropdowns.groupBy.options,
            }),
          );
        }
      }
      if (localConfig && localConfig.config && (localConfig.config.columns || localConfig.config.view)) {
        const view = localConfig.config as unknown as ConfigurableGridViewDefn;
        // This must be done specially, as it doesn't use the subheader groupBy
        if (view.subheaderDropdowns && view.subheaderDropdowns.groupBy) {
          if (!isNil(localConfig.groupBySelection)) {
            dispatch(updateGroupBy(localConfig.groupBySelection));
          } else {
            const subHCurrentSelection = getState().subheader.groupBy.selection;
            const defaultSelection = view.subheaderDropdowns.groupBy.defaultSelection;
            const currentSelection = coalesce(subHCurrentSelection, defaultSelection) ?? 0;
            dispatch(updateGroupBy(currentSelection));
          }
        }
        if (!isNil(view.configure)) {
          if (localConfig.configurationSelections) {
            dispatch(updateConfigureSelections(localConfig.configurationSelections));
          } else {
            const { defaultSelections } = parseConfigureConfig(view.configure as any);
            dispatch(updateConfigureSelections(defaultSelections));
          }
        }
      } else {
        // This code is cloned because we fall back to base viewdefn if nothing present in favorites
        if (config.subheaderDropdowns && config.subheaderDropdowns.groupBy) {
          const groupByConfig = config.subheaderDropdowns.groupBy;
          // Denver, what is going on here??
          // Configurable grid uses it's own subheader options and selection separate from other views,
          // and we haven't merged it in with subheader selections yet, despite it being nearly identical.
          // Because someone wants group bys to be preserved between views,
          // we need to consume three things when trying to figure out the current group by:
          // 1. The current config grid group by
          // 2. The current subheader group by
          // 3. The default selected config grid group by
          // Then, very carefully, fuse those three things togethter to determine the correct current group by

          // first figure out the incoming subheader option, and if it's dataIndex is in configgrid group by
          let subheaderDataIndex: string | undefined = getGroupBySelectedOptionProperty(
            getState().subheader.groupBy,
            'dataIndex',
          );
          const incomingSelectionInCurrentOptions = groupByConfig.options.find(
            (o: { dataIndex: string }) => o.dataIndex === subheaderDataIndex,
          );
          if (!incomingSelectionInCurrentOptions) {
            // if the incoming subheader option isn't in the new set of options, dump it
            subheaderDataIndex = undefined;
          }

          // finally get the default
          const defaultDataIndex = get(groupByConfig.options, groupByConfig.defaultSelection)?.dataIndex;

          // fuse them all together
          const coalescedDataIndex =
            coalesce(
              subheaderDataIndex === '' ? undefined : subheaderDataIndex, // getGroupBySelectedOptionProperty returns '' if the option isn't found, so fix that here
              defaultDataIndex,
            ) ?? '';

          // now that we have the dataIndex, turn it back into an array index
          const coalescedIndex = groupByConfig.options.findIndex(
            (o: { dataIndex: string }) => o.dataIndex === coalescedDataIndex,
          );
          dispatch(updateGroupBy(coalescedIndex));
        }
        if (!isNil(config.configure)) {
          const { defaultSelections } = parseConfigureConfig(config.configure as any);
          dispatch(updateConfigureSelections(defaultSelections));
        }
      }
      const configWithFavorites = localConfig && localConfig.config ? localConfig.config : config;

      dispatch(receiveConfigurableGridConfig({ unmodifiedViewDefn: configResponse[0], viewDefn: configWithFavorites }));
    } catch (error) {
      const err = error as any;
      dispatch(
        receiveError({
          type: ComponentErrorType.config,
          message: formatViewDefnError(error),
          name: ConfDefnComponentType.configurableGrid,
          issues: error,
          defnId: err.defnId,
        }),
      );
    }
  };
}

export function fetchFloorsetData(floorsetApi: ClientDataApi | undefined) {
  return async (dispatch: AppThunkDispatch, getState: () => AppState): Promise<BaseAction | void> => {
    if (isNil(floorsetApi)) {
      // if there is no magic floorset dropdown, skip the rest of this and let the function go to
      // fetchConfigurableGridData(), where we will attempt to make a listData call without
      // sending in the floorset information
      dispatch(refreshConfigurableGridData());
      return;
    }

    dispatch(requestFloorsetData());

    const { url: floorsetUrl, headers: floorsetHeaders, params: floorsetParams } = floorsetApi;
    const floorsetOptions = {
      headers: floorsetHeaders,
      params: floorsetParams,
    };
    const currentAppName = getState().perspective.selected?.appType;
    if (!currentAppName) throw new Error('AppName not found, this should not happen');
    set(floorsetOptions, 'params.appName', currentAppName);

    try {
      const floorsets = await Axios.get(floorsetUrl, floorsetOptions).then((resp) => {
        if (isNil(resp.data.data) || resp.data.success === false) {
          // this endpoint doesn't return 5xx when it fails, it returns success: false with a 200 response
          // so manually throw here in that case
          throw new Error('Failed to fetch floorsetdata');
        }
        return resp.data.data as BasicItem[];
      });
      dispatch(receiveTopMemberDropdownData(floorsets));
      dispatch(receiveFloorsetData());
    } catch (error) {
      const err = error as any;
      dispatch(
        receiveError({
          type: ComponentErrorType.data,
          message: (error as Error)?.message,
          name: ConfDefnComponentType.configurableGrid,
          stack: (error as Error)?.stack,
          issues: error,
          defnId: err.defnId,
        }),
      );
    }
  };
}

const scopeSelectionsToCommaList = (
  selections: ScopeSlice['scope'],
  topMembers: string | undefined,
  floorsetId: string | undefined,
): string => {
  return concat(
    [],
    defaultTo(floorsetId, []),
    defaultTo(topMembers, defaultTo(selections.productMember, [])),
    defaultTo(selections.locationMember, []),
  ).join(',');
};

const getDefaultFloorsetId = (
  topMemberSelection: string | undefined,
  topMemberOptions: BasicItem[] | undefined,
  floorsetApi: ConfigurableGridOwnProps['floorsetApi'],
): string | undefined => {
  let floorsetId: string | undefined;

  const isSelectionValid = topMemberOptions?.some((item) => item.id === topMemberSelection);

  if (!isNil(topMemberOptions) && !isNil(floorsetApi)) {
    // Check if floorsetApi before trying to get floorsetId
    const options = parseFloorsetDropdownData(topMemberOptions);
    const floorsetIndex = !isSelectionValid ? 0 : findIndex(options, (option) => option.id === topMemberSelection);
    floorsetId = options[floorsetIndex]?.id || '';
  }
  return floorsetId;
};

const getFinalDefnId = (topAttributesApi: ConfigurableGridOwnProps['topAttributesApi']): string => {
  return isListDataApi(topAttributesApi)
    ? topAttributesApi.defnId // prefer this version for config setup, else fallback to handle legacy configuration
    : defaultTo(topAttributesApi?.params?.defnId, '');
};

// Consistent topMember coalesce (taken from `fetchConfigurableGridData`) for use by all the relevant data fetches in confgrid
function fetchTopMember(state: AppState, ownProps: ConfigurableGridOwnProps) {
  const alternateTopMember = state.worklist.selectedItemId;
  const queryParamTopMember = topMemInUrl();
  const propTopMembers = ownProps.topMembers;
  return propTopMembers ?? queryParamTopMember ?? alternateTopMember;
}

export function fetchConfigurableGridData(ownProps: ConfigurableGridOwnProps) {
  return async (dispatch: AppThunkDispatch, getState: () => AppState): Promise<BaseAction | void> => {
    const scope = getState().scope;
    const { topMemberSelection, topMemberOptions } = getState().subheader;
    const { dataApi, topAttributesApi, floorsetApi } = ownProps;

    const needsTopMember = isWorklistActive(getState());
    const finalTopMembers = fetchTopMember(getState(), ownProps);
    const floorsetId = getDefaultFloorsetId(topMemberSelection, topMemberOptions, floorsetApi);

    // if theres no topMember, early return to prevent errors
    if (needsTopMember && (!finalTopMembers || finalTopMembers.trim() === '')) {
      return;
    }

    dispatch(requestGridData());

    const aggBy = dataApi.params?.aggBy;

    let formattedDataProm: Promise<BasicPivotItem[]>;

    // **Fetch extra pivot params from subheader state**
    const extraPivotParams = getExtraPivotParams(getState().subheader);

    // ConfigGrid supports toplevel api calls in both /floorset and /listData mode
    // /floorset requires the floorset list be called first and sent in as a topMember
    // while listData works differently
    if (isListDataApi(dataApi)) {
      const listDataTopMembers = reject([finalTopMembers, floorsetId], isNil).join(',');
      formattedDataProm = service.pivotService
        .listData(dataApi.defnId, AppType.Assortment, {
          ...dataApi.params,
          aggBy,
          nestData: false, // we require flat responses for ConfigurableGrid
          topMembers: listDataTopMembers,
          ...extraPivotParams,
        })
        .then((resp) => resp.tree);
    } else {
      const currentAppName = getState().perspective.selected?.appType;
      if (!currentAppName) throw new Error('AppName not found, this should not happen');
      const { url: dataUrl, headers: dataHeaders, params: dataParams = {} } = dataApi;
      const dataOptions = {
        headers: dataHeaders,
        params: {
          ...dataParams,
          aggBy,
          floorsetId,
          topMembers: finalTopMembers,
          appName: currentAppName,
          ...extraPivotParams,
        },
      };
      formattedDataProm = Axios.get(dataUrl, dataOptions)
        .then(async (resp) => {
          const dataResponse = resp.data;
          return isString(dataResponse) ? service.pivotService.deserialize(dataResponse) : dataResponse.list;
        })
        .then((formattedData: BasicPivotItem[]) => {
          // If we are dealing with a nested set, first level must be single item, gets second level
          // (/floorset returns [{channel: ..., children: [<row_records>]}])
          if (formattedData && formattedData[0] && formattedData[0].children && formattedData[0].children.length > 0) {
            return formattedData[0].children;
          } else {
            return formattedData;
          }
        });
    }

    // Add in topAttributes call for floorset
    const members = scopeSelectionsToCommaList(scope.scope, finalTopMembers, floorsetId);

    const finalDefnId = getFinalDefnId(topAttributesApi);

    const topAttributesProm: Promise<BasicPivotItem | undefined> = isEmpty(finalDefnId)
      ? Promise.resolve(undefined)
      : service.pivotService
          .listData(finalDefnId, ASSORTMENT, { topMembers: members })
          .then((attributes) => attributes.tree[0]);

    try {
      const [formattedData, topAttributes] = await Promise.all([formattedDataProm, topAttributesProm]);
      // Trim whitespace from the values within formattedData
      formattedData &&
        formattedData.forEach((attr: BasicPivotItem) => {
          Object.keys(attr).map((k) => (isString(attr[k]) ? (attr[k] = attr[k].trim()) : (attr[k] = attr[k])));
        });

      dispatch(receiveGridData(formattedData));
      dispatch(receiveTopAttributesData(topAttributes!));
    } catch (error) {
      const err = error as any;
      dispatch(
        receiveError({
          type: ComponentErrorType.data,
          message: (error as Error)?.message,
          name: ConfDefnComponentType.configurableGrid,
          stack: (error as Error)?.stack,
          issues: error,
          defnId: err.defnId,
        }),
      );
    }
  };
}

export function fetchConfigurableGridTopAttributes(ownProps: ConfigurableGridOwnProps) {
  return async (dispatch: AppThunkDispatch, getState: () => AppState): Promise<BaseAction | void> => {
    const { topAttributesApi, topMembers = undefined, floorsetApi } = ownProps;
    const { topMemberSelection, topMemberOptions } = getState().subheader;
    const floorsetId = getDefaultFloorsetId(topMemberSelection, topMemberOptions, floorsetApi);
    const finalTopMembers = fetchTopMember(getState(), ownProps);
    const members = scopeSelectionsToCommaList(getState().scope.scope, finalTopMembers, floorsetId);

    const finalDefnId = getFinalDefnId(topAttributesApi);

    const topAttributesProm: Promise<BasicPivotItem | undefined> = isEmpty(finalDefnId)
      ? Promise.resolve(undefined)
      : service.pivotService
          .listData(finalDefnId, ASSORTMENT, { topMembers: members })
          .then((attributes) => attributes.tree[0]);

    try {
      const topAttributes = await topAttributesProm;

      dispatch(receiveTopAttributesData(topAttributes!));
    } catch (error) {
      const err = error as any;
      dispatch(
        receiveError({
          type: ComponentErrorType.data,
          message: (error as Error)?.message,
          name: ConfDefnComponentType.configurableGrid,
          stack: (error as Error)?.stack,
          issues: error,
          defnId: err.defnId,
        }),
      );
    }
  };
}

export function submitConfigurableGridPayload(payload: CoarseEditPayload) {
  return service.pivotService.coarseEditSubmitData(payload);
}

export default configurableGridSlice.reducer;
